import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

// Permission definitions matching backend
const PERMISSIONS = {
  // Projects
  "project:create": ["admin", "manager"],
  "project:update": ["admin", "manager"],
  "project:delete": ["admin"],
  
  // Tasks
  "task:create": ["admin", "manager", "contributor"],
  "task:update": ["admin", "manager", "contributor"],
  "task:delete": ["admin", "manager"],
  
  // Content
  "content:create": ["admin", "manager", "contributor"],
  "content:update": ["admin", "manager", "contributor"],
  "content:delete": ["admin", "manager"],
  
  // Events
  "event:create": ["admin", "manager"],
  "event:update": ["admin", "manager"],
  "event:delete": ["admin"],
  
  // Assets
  "asset:create": ["admin", "manager", "contributor"],
  "asset:update": ["admin", "manager", "contributor"],
  "asset:delete": ["admin", "manager"],
  
  // Approvals
  "approval:create": ["admin", "manager", "contributor"],
  "approval:update": ["admin", "manager"],
  
  // Users
  "user:manage": ["admin"],
  
  // Reports
  "report:view": ["admin", "manager", "contributor", "viewer"],
};

const ROLE_HIERARCHY = {
  admin: 4,
  manager: 3,
  contributor: 2,
  viewer: 1
};

export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    if (user?.role) {
      const userPermissions = {};
      Object.entries(PERMISSIONS).forEach(([perm, roles]) => {
        userPermissions[perm] = roles.includes(user.role);
      });
      setPermissions(userPermissions);
    }
  }, [user?.role]);

  // Check if user has a specific permission
  const can = useCallback((permission) => {
    return permissions[permission] === true;
  }, [permissions]);

  // Check if user has minimum role level
  const hasMinRole = useCallback((minRole) => {
    const userLevel = ROLE_HIERARCHY[user?.role] || 0;
    const requiredLevel = ROLE_HIERARCHY[minRole] || 0;
    return userLevel >= requiredLevel;
  }, [user?.role]);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return user?.role === 'admin';
  }, [user?.role]);

  // Check if user is at least manager
  const isManager = useCallback(() => {
    return ['admin', 'manager'].includes(user?.role);
  }, [user?.role]);

  // Check if user can edit (not viewer)
  const canEdit = useCallback(() => {
    return user?.role !== 'viewer';
  }, [user?.role]);

  const value = {
    permissions,
    can,
    hasMinRole,
    isAdmin,
    isManager,
    canEdit,
    role: user?.role
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

// HOC for permission-based rendering
export const withPermission = (WrappedComponent, permission) => {
  return function PermissionWrapper(props) {
    const { can } = usePermissions();
    
    if (!can(permission)) {
      return null;
    }
    
    return <WrappedComponent {...props} />;
  };
};
